import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setZendeskConfigAction } from '../../../app/appActions';
import { getZendeskSnippetKeySelector } from '../zendeskSelectors';
import useZendeskWebWidgetInstance from "./useZendeskWebWidgetInstance";
import { getAppFeatures } from "../../../app/appSelectors";
import { FEATURES } from "../../../app/appConstants";
import _ from "lodash";

export const useZendeskScriptInit = () => {
    const onWidgetCloseCallback = useRef<() => void | null>(null);
    const zendeskSnippetKey = useSelector(getZendeskSnippetKeySelector);
    const zendeskWebWidgetInstance = useZendeskWebWidgetInstance();
    const features = useSelector(getAppFeatures);
    const dispatch = useDispatch();

    const setOnWidgetCloseCallback = useCallback((callback: () => void) => {
        onWidgetCloseCallback.current = callback;
    }, []);

    const setZendeskUnreadMessagesCount = useCallback((count) => {
        dispatch(setZendeskConfigAction({ unreadMessagesCount: count }));
    }, [dispatch]);

    const debounceSetZendeskUnreadMessagesCount = useMemo(() =>
        // zendesk on init send count: 1 then actual count, so we debounce it
        _.debounce(setZendeskUnreadMessagesCount, 2000), [setZendeskUnreadMessagesCount])

    useEffect(() => {
        if (!features[FEATURES.SUPPORT]) {
            return;
        }
        let script: HTMLScriptElement;
        if (zendeskSnippetKey) {
            const url = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskSnippetKey}`;
            script = document.createElement('script');
            script.src = url;
            script.id = 'ze-snippet';
            script.async = true;
            document.body.appendChild(script);
            script.onload = function () {
                zendeskWebWidgetInstance.init(onWidgetCloseCallback.current);
                zendeskWebWidgetInstance.onUnreadMessages((count) => {
                    dispatch(debounceSetZendeskUnreadMessagesCount(count));
                });
                dispatch(setZendeskConfigAction({ isInited: true }));
            };
            return () => {
                document.body.removeChild(script);
            };
        }
    }, [debounceSetZendeskUnreadMessagesCount, dispatch, features, zendeskSnippetKey, zendeskWebWidgetInstance]);
    return { setOnWidgetCloseCallback };
};
